import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import AdminSidebar from '../../components/AdminSidebar';
import AdminTopBar from '../../components/AdminTopBar';
import ProfessionalKpiCards from '../../components/admin/ProfessionalKpiCards';
import { API_BASE_URL } from '../../config';

const getAdminHeaders = () => {
  const token = localStorage.getItem('adminToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const MODULE_LABELS = {
  global: 'Global Premium',
  alakart: 'Alakart',
  allergen: 'Alerjen',
  diet: 'Diyet',
  athlete: 'Sporcu',
  special_days: 'Özel Günler'
};

const PRICING_MODULE_OPTIONS = ['allergen', 'diet', 'athlete', 'alakart', 'special_days'];

const INITIAL_NEW_PRICING_PLAN = {
  planKey: '',
  title: '',
  description: '',
  moduleSet: ['allergen'],
  durationMonths: 1,
  amountTRY: 149,
  sortOrder: 0,
  isActive: true
};

const normalizePlanKeyText = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/ı/g, 'i')
    .replace(/ğ/g, 'g')
    .replace(/ü/g, 'u')
    .replace(/ş/g, 's')
    .replace(/ö/g, 'o')
    .replace(/ç/g, 'c')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .replace(/_+/g, '_');

const GRANTABLE_MODULE_LABELS = {
  global: 'Global Premium',
  alakart: 'Alakart',
  allergen: 'Alerjen',
  diet: 'Diyet',
  athlete: 'Sporcu',
  special_days: 'Özel Günler'
};

const BILLING_LABELS = {
  eft: 'EFT',
  manual: 'Manuel',
  complimentary: 'Hediye'
};

const SCOPE_LABELS = {
  household: 'Hane Geneli',
  member: 'Üye Bazlı'
};

const TAB_ITEMS = [
  { key: 'subscriptions', label: 'Abonelikler' },
  { key: 'proKpi', label: 'Profesyonel KPI' },
  { key: 'payments', label: 'Ödeme Talepleri' },
  { key: 'specialAccess', label: 'Özel Günler Hakları' },
  { key: 'pricing', label: 'Fiyat Planları' }
];

function formatDateTime(value) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return date.toLocaleString('tr-TR');
}

function getDaysRemaining(value) {
  if (!value) return null;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return Math.ceil((date.getTime() - Date.now()) / (1000 * 60 * 60 * 24));
}

function StatCard({ title, value, tone = 'slate' }) {
  const tones = {
    slate: 'border-slate-200 dark:border-slate-800 bg-white dark:bg-[#101922]',
    green: 'border-green-200 dark:border-green-900/30 bg-green-50 dark:bg-green-900/10',
    amber: 'border-amber-200 dark:border-amber-900/30 bg-amber-50 dark:bg-amber-900/10',
    red: 'border-red-200 dark:border-red-900/30 bg-red-50 dark:bg-red-900/10',
    blue: 'border-sky-200 dark:border-sky-900/30 bg-sky-50 dark:bg-sky-900/10'
  };

  return (
    <div className={`rounded-xl border p-5 shadow-sm ${tones[tone] || tones.slate}`}>
      <div className="text-sm font-medium text-slate-500 dark:text-slate-400">{title}</div>
      <div className="mt-2 text-2xl font-bold text-slate-900 dark:text-white">{value}</div>
    </div>
  );
}

function TabButton({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={[
        'rounded-full px-4 py-2 text-sm font-semibold transition',
        active
          ? 'bg-slate-900 text-white shadow-sm dark:bg-white dark:text-slate-900'
          : 'bg-white text-slate-600 hover:bg-slate-100 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800'
      ].join(' ')}
    >
      {children}
    </button>
  );
}

function SectionCard({ title, description, right, children }) {
  return (
    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-[#101922]">
      <div className="flex flex-col gap-3 border-b border-slate-200 px-5 py-4 dark:border-slate-800 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-base font-semibold text-slate-900 dark:text-white">{title}</h2>
          {description ? <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{description}</p> : null}
        </div>
        {right}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

export default function AdminSubscriptions() {
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('subscriptions');
  const [paymentUnreadCount, setPaymentUnreadCount] = useState(0);
  const [professionalUnreadCount, setProfessionalUnreadCount] = useState(0);
  const [users, setUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [quickFilter, setQuickFilter] = useState('all');
  const [filters, setFilters] = useState({
    status: 'all',
    moduleKey: 'all',
    billingType: 'all',
    search: ''
  });
  const [createForm, setCreateForm] = useState({
    userId: '',
    moduleKey: 'global',
    targetMemberKey: 'household',
    scopeType: 'household',
    durationPreset: '1',
    startedAt: new Date().toISOString().slice(0, 16),
    expiresAt: '',
    billingType: 'eft',
    paymentReference: '',
    note: ''
  });
  const [summary, setSummary] = useState(null);
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoadingId, setActionLoadingId] = useState(null);
  const [createLoading, setCreateLoading] = useState(false);
  const [createSuccess, setCreateSuccess] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [detailData, setDetailData] = useState(null);
  const [pricingPlans, setPricingPlans] = useState([]);
  const [pricingLoading, setPricingLoading] = useState(false);
  const [pricingError, setPricingError] = useState(null);
  const [pricingSuccess, setPricingSuccess] = useState(null);
  const [pricingSaveId, setPricingSaveId] = useState(null);
  const [pricingBootstrapLoading, setPricingBootstrapLoading] = useState(false);
  const [pricingDeleteId, setPricingDeleteId] = useState(null);
  const [pricingCreateOpen, setPricingCreateOpen] = useState(false);
  const [pricingCreateLoading, setPricingCreateLoading] = useState(false);
  const [newPricingPlan, setNewPricingPlan] = useState(INITIAL_NEW_PRICING_PLAN);
  const [paymentIntents, setPaymentIntents] = useState([]);
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [paymentFilters, setPaymentFilters] = useState({ status: 'all', search: '' });
  const [paymentActionLoadingId, setPaymentActionLoadingId] = useState(null);
  const [specialAccesses, setSpecialAccesses] = useState([]);
  const [specialAccessSummary, setSpecialAccessSummary] = useState(null);
  const [specialAccessLoading, setSpecialAccessLoading] = useState(false);
  const [specialAccessError, setSpecialAccessError] = useState(null);
  const [specialAccessFilters, setSpecialAccessFilters] = useState({ status: 'all', search: '' });
  const [specialAccessActionLoadingId, setSpecialAccessActionLoadingId] = useState(null);
  const [proKpi, setProKpi] = useState(null);
  const [proKpiLoading, setProKpiLoading] = useState(false);
  const [proKpiError, setProKpiError] = useState(null);
  const [proPlanCatalog, setProPlanCatalog] = useState([]);
  const [proPlanLoading, setProPlanLoading] = useState(false);
  const [proPlanError, setProPlanError] = useState(null);
  const [proPlanSuccess, setProPlanSuccess] = useState(null);
  const [proPlanSaveId, setProPlanSaveId] = useState(null);
  const [proProfiles, setProProfiles] = useState([]);
  const [proSubscriptions, setProSubscriptions] = useState([]);
  const [proRelationships, setProRelationships] = useState([]);
  const [proOpsLoading, setProOpsLoading] = useState(false);
  const [proOpsError, setProOpsError] = useState(null);

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    if (filters.status) params.set('status', filters.status);
    if (filters.moduleKey) params.set('moduleKey', filters.moduleKey);
    if (filters.billingType) params.set('billingType', filters.billingType);
    if (filters.search.trim()) params.set('search', filters.search.trim());
    return params.toString();
  }, [filters]);

  const filteredUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    if (!q) return users.slice(0, 200);
    return users
      .filter((user) => `${user.name || ''} ${user.email || ''} ${user.id || ''}`.toLowerCase().includes(q))
      .slice(0, 100);
  }, [users, userSearch]);

  const visibleGrants = useMemo(() => {
    if (quickFilter !== 'expiringSoon') return grants;
    return grants.filter((grant) => {
      const remaining = getDaysRemaining(grant.expiresAt);
      return grant.status === 'active' && remaining !== null && remaining >= 0 && remaining <= 7;
    });
  }, [grants, quickFilter]);

  const pricingSummary = useMemo(() => {
    const total = pricingPlans.length;
    const active = pricingPlans.filter((plan) => plan.isActive).length;
    const singles = pricingPlans.filter((plan) => Array.isArray(plan.moduleSet) && plan.moduleSet.length === 1).length;
    const bundles = pricingPlans.filter((plan) => Array.isArray(plan.moduleSet) && plan.moduleSet.length > 1).length;
    return { total, active, singles, bundles };
  }, [pricingPlans]);

  const proOpsSummary = useMemo(() => {
    const total = proProfiles.length;
    const dietitianCount = proProfiles.filter((p) => p.professionalType === 'dietitian').length;
    const ptCount = proProfiles.filter((p) => p.professionalType === 'pt').length;
    const pendingCount = proProfiles.filter((p) => p.status === 'pending_verification').length;
    const verifiedCount = proProfiles.filter((p) => p.status === 'verified').length;
    const activeSubscriptionCount = proSubscriptions.filter((s) => s.status === 'active').length;
    return {
      total,
      dietitianCount,
      ptCount,
      pendingCount,
      verifiedCount,
      activeSubscriptionCount,
      activeRelationshipCount: proRelationships.length,
    };
  }, [proProfiles, proSubscriptions, proRelationships]);

  const paymentQueryString = useMemo(() => {
    const params = new URLSearchParams();
    if (paymentFilters.status) params.set('status', paymentFilters.status);
    if (paymentFilters.search.trim()) params.set('search', paymentFilters.search.trim());
    return params.toString();
  }, [paymentFilters]);

  const specialAccessQueryString = useMemo(() => {
    const params = new URLSearchParams();
    params.set('moduleKey', 'special_days');
    if (specialAccessFilters.status) params.set('status', specialAccessFilters.status);
    if (specialAccessFilters.search.trim()) params.set('search', specialAccessFilters.search.trim());
    return params.toString();
  }, [specialAccessFilters]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(`${API_BASE_URL}/api/admin/subscriptions?${queryString}`, {
        credentials: 'include',
        headers: getAdminHeaders()
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Abonelik verileri alinamadi');
      setSummary(data.summary || null);
      setGrants(Array.isArray(data.grants) ? data.grants : []);
    } catch (err) {
      console.error('admin subscriptions load error', err);
      setError(err.message || 'Abonelik verileri alinamadi');
    } finally {
      setLoading(false);
    }
  };

  const loadPricingPlans = async () => {
    try {
      setPricingLoading(true);
      setPricingError(null);
      const res = await fetch(`${API_BASE_URL}/api/admin/pricing-plans`, {
        credentials: 'include',
        headers: getAdminHeaders()
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Fiyat planlari alinamadi');
      setPricingPlans(Array.isArray(data.plans) ? data.plans : []);
    } catch (err) {
      console.error('admin pricing plans load error', err);
      setPricingError(err.message || 'Fiyat planlari alinamadi');
    } finally {
      setPricingLoading(false);
    }
  };

  const loadPaymentIntents = async () => {
    try {
      setPaymentLoading(true);
      setPaymentError(null);
      const res = await fetch(`${API_BASE_URL}/api/admin/payment-intents?${paymentQueryString}`, {
        credentials: 'include',
        headers: getAdminHeaders()
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Ödeme talepleri alınamadı');
      setPaymentIntents(Array.isArray(data.intents) ? data.intents : []);
      setPaymentSummary(data.summary || null);
      setPaymentUnreadCount(Number(data.summary?.receiptUploaded || 0) + Number(data.summary?.manualReview || 0));
    } catch (err) {
      console.error('admin payment intents load error', err);
      setPaymentError(err.message || 'Ödeme talepleri alınamadı');
    } finally {
      setPaymentLoading(false);
    }
  };

  const loadPaymentNotificationCount = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/payment-intents?status=all`, {
        credentials: 'include',
        headers: getAdminHeaders()
      });
      if (!res.ok) return;
      const data = await res.json().catch(() => ({}));
      setPaymentUnreadCount(Number(data.summary?.receiptUploaded || 0) + Number(data.summary?.manualReview || 0));
    } catch (err) {
      console.warn('admin payment notification count error', err);
    }
  };

  const loadProfessionalNotificationCount = async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/admin/pro-subscriptions`, {
        credentials: 'include',
        headers: getAdminHeaders()
      });
      if (!res.ok) return;
      const data = await res.json().catch(() => ({}));
      const items = Array.isArray(data.items) ? data.items : [];
      const awaiting = items.filter((item) => ['trialing', 'inactive', 'past_due', 'suspended'].includes(String(item?.status || '').toLowerCase())).length;
      setProfessionalUnreadCount(awaiting);
    } catch (err) {
      console.warn('admin professional notification count error', err);
    }
  };

  const loadSpecialAccesses = async () => {
    try {
      setSpecialAccessLoading(true);
      setSpecialAccessError(null);
      const res = await fetch(`${API_BASE_URL}/api/admin/special-kitchen-accesses?${specialAccessQueryString}`, {
        credentials: 'include',
        headers: getAdminHeaders()
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Özel Günler hakları alınamadı');
      setSpecialAccesses(Array.isArray(data.accesses) ? data.accesses : []);
      setSpecialAccessSummary(data.summary || null);
    } catch (err) {
      console.error('admin special accesses load error', err);
      setSpecialAccessError(err.message || 'Özel Günler hakları alınamadı');
    } finally {
      setSpecialAccessLoading(false);
    }
  };

  const loadProKpiOverview = async () => {
    try {
      setProKpiLoading(true);
      setProKpiError(null);
      const res = await fetch(`${API_BASE_URL}/api/admin/pro-kpi/overview`, {
        credentials: 'include',
        headers: getAdminHeaders()
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Professional KPI verisi alinamadi');
      setProKpi(data.metrics || null);
    } catch (err) {
      console.error('admin pro kpi load error', err);
      setProKpiError(err.message || 'Professional KPI verisi alinamadi');
    } finally {
      setProKpiLoading(false);
    }
  };

  const loadProPlanCatalog = async () => {
    try {
      setProPlanLoading(true);
      setProPlanError(null);
      const res = await fetch(`${API_BASE_URL}/api/admin/pro-plan-catalog`, {
        credentials: 'include',
        headers: getAdminHeaders()
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Profesyonel planlar alinamadi');
      setProPlanCatalog(Array.isArray(data.items) ? data.items : []);
    } catch (err) {
      console.error('admin pro plan catalog load error', err);
      setProPlanError(err.message || 'Profesyonel planlar alinamadi');
    } finally {
      setProPlanLoading(false);
    }
  };

  const loadProfessionalOps = async () => {
    try {
      setProOpsLoading(true);
      setProOpsError(null);
      const [profilesRes, subscriptionsRes, relRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/admin/professionals`, {
          credentials: 'include',
          headers: getAdminHeaders()
        }),
        fetch(`${API_BASE_URL}/api/admin/pro-subscriptions`, {
          credentials: 'include',
          headers: getAdminHeaders()
        }),
        fetch(`${API_BASE_URL}/api/admin/client-relationships?status=active`, {
          credentials: 'include',
          headers: getAdminHeaders()
        }),
      ]);

      const profilesData = await profilesRes.json().catch(() => ({}));
      const subscriptionsData = await subscriptionsRes.json().catch(() => ({}));
      const relData = await relRes.json().catch(() => ({}));

      if (!profilesRes.ok) throw new Error(profilesData.error || 'Uzman listesi alınamadı');
      if (!subscriptionsRes.ok) throw new Error(subscriptionsData.error || 'Uzman abonelikleri alınamadı');
      if (!relRes.ok) throw new Error(relData.error || 'Aktif ilişki listesi alınamadı');

      setProProfiles(Array.isArray(profilesData.items) ? profilesData.items : []);
      setProSubscriptions(Array.isArray(subscriptionsData.items) ? subscriptionsData.items : []);
      setProRelationships(Array.isArray(relData.items) ? relData.items : []);
    } catch (err) {
      console.error('admin professional ops load error', err);
      setProOpsError(err.message || 'Profesyonel operasyon verisi alınamadı');
    } finally {
      setProOpsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [queryString]);

  useEffect(() => {
    if (activeTab === 'proKpi') {
      loadProKpiOverview();
      loadProfessionalOps();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'pricing') {
      loadPricingPlans();
      loadProPlanCatalog();
    }
  }, [activeTab]);

  useEffect(() => {
    if (activeTab === 'payments') {
      loadPaymentIntents();
    }
  }, [activeTab, paymentQueryString]);

  useEffect(() => {
    if (activeTab === 'specialAccess') {
      loadSpecialAccesses();
    }
  }, [activeTab, specialAccessQueryString]);

  useEffect(() => {
    loadPaymentNotificationCount();
    loadProfessionalNotificationCount();
    const handleRefresh = () => {
      loadPaymentNotificationCount();
      loadProfessionalNotificationCount();
    };
    window.addEventListener('refreshNotifications', handleRefresh);
    window.addEventListener('realtime:notifications-updated', handleRefresh);
    window.addEventListener('realtime:payment-updated', handleRefresh);
    window.addEventListener('realtime:force-refetch', handleRefresh);
    const interval = setInterval(loadPaymentNotificationCount, 300000);
    const proInterval = setInterval(loadProfessionalNotificationCount, 300000);
    return () => {
      window.removeEventListener('refreshNotifications', handleRefresh);
      window.removeEventListener('realtime:notifications-updated', handleRefresh);
      window.removeEventListener('realtime:payment-updated', handleRefresh);
      window.removeEventListener('realtime:force-refetch', handleRefresh);
      clearInterval(interval);
      clearInterval(proInterval);
    };
  }, []);

  useEffect(() => {
    const requestedTab = location.state?.adminTab;
    if (requestedTab && TAB_ITEMS.some((tab) => tab.key === requestedTab)) {
      setActiveTab(requestedTab);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.pathname, location.state, navigate]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/admin/users`, {
          credentials: 'include',
          headers: getAdminHeaders()
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(data.error || 'Kullanici listesi alinamadi');
        if (mounted) setUsers(Array.isArray(data.users) ? data.users : []);
      } catch (err) {
        console.error('admin subscription users load error', err);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreateSuccess(null);
    setError(null);

    if (!createForm.userId) {
      setError('Once kullanici secmelisin.');
      return;
    }

    try {
      setCreateLoading(true);
      const payload = {
        moduleKey: createForm.moduleKey,
        targetMemberKey: createForm.targetMemberKey || 'household',
        scopeType: createForm.scopeType || 'household',
        billingType: createForm.billingType,
        startedAt: createForm.startedAt,
        paymentReference: createForm.paymentReference || null,
        note: createForm.note || null
      };

      if (createForm.durationPreset === 'custom') {
        payload.expiresAt = createForm.expiresAt;
      } else {
        payload.months = Number(createForm.durationPreset);
      }

      const res = await fetch(`${API_BASE_URL}/api/admin/user/${createForm.userId}/subscriptions`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAdminHeaders() },
        body: JSON.stringify(payload)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || data.message || 'Abonelik tanimlanamadi');

      setCreateSuccess('Abonelik basariyla tanimlandi.');
      setCreateForm((prev) => ({ ...prev, paymentReference: '', note: '', expiresAt: '' }));
      await loadData();
    } catch (err) {
      console.error('admin subscription create error', err);
      setError(err.message || 'Abonelik tanimlanamadi');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleCancel = async (grantId) => {
    try {
      setActionLoadingId(grantId);
      const res = await fetch(`${API_BASE_URL}/api/admin/subscriptions/${grantId}/cancel`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAdminHeaders() }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Abonelik iptal edilemedi');
      await loadData();
    } catch (err) {
      console.error('admin subscription cancel error', err);
      setError(err.message || 'Abonelik iptal edilemedi');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleExtend = async (grantId, months) => {
    try {
      setActionLoadingId(`${grantId}:${months}`);
      const res = await fetch(`${API_BASE_URL}/api/admin/subscriptions/${grantId}/extend`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAdminHeaders() },
        body: JSON.stringify({ months })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Abonelik uzatilamadi');
      await loadData();
    } catch (err) {
      console.error('admin subscription extend error', err);
      setError(err.message || 'Abonelik uzatilamadi');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handleOpenDetail = async (grantId) => {
    try {
      setDetailOpen(true);
      setDetailLoading(true);
      setDetailError(null);
      setDetailData(null);
      const res = await fetch(`${API_BASE_URL}/api/admin/subscriptions/${grantId}`, {
        credentials: 'include',
        headers: getAdminHeaders()
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Abonelik detayi alinamadi');
      setDetailData(data);
    } catch (err) {
      console.error('admin subscription detail error', err);
      setDetailError(err.message || 'Abonelik detayi alinamadi');
    } finally {
      setDetailLoading(false);
    }
  };

  const handleBootstrapPricingPlans = async () => {
    try {
      setPricingBootstrapLoading(true);
      setPricingError(null);
      setPricingSuccess(null);
      const res = await fetch(`${API_BASE_URL}/api/admin/pricing-plans/bootstrap`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAdminHeaders() }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Varsayılan planlar oluşturulamadı');
      setPricingPlans(Array.isArray(data.plans) ? data.plans : []);
      setPricingSuccess('Varsayılan fiyat planları hazırlandı.');
    } catch (err) {
      console.error('admin pricing bootstrap error', err);
      setPricingError(err.message || 'Varsayılan planlar oluşturulamadı');
    } finally {
      setPricingBootstrapLoading(false);
    }
  };

  const handlePlanFieldChange = (planId, field, value) => {
    setPricingPlans((current) => current.map((plan) => (plan.id === planId ? { ...plan, [field]: value } : plan)));
  };

  const handleNewPricingPlanChange = (field, value) => {
    setNewPricingPlan((current) => ({ ...current, [field]: value }));
  };

  const handleGeneratePlanKey = () => {
    const titlePart = normalizePlanKeyText(newPricingPlan.title || '');
    const duration = Number(newPricingPlan.durationMonths);
    const durationPart = Number.isFinite(duration) && duration >= 0 ? `${duration}m` : null;
    const generated = normalizePlanKeyText([titlePart, durationPart].filter(Boolean).join('_')) || `custom_plan_${Date.now()}`;
    setNewPricingPlan((current) => ({ ...current, planKey: generated }));
  };

  const handleNewPricingModuleToggle = (moduleKey) => {
    setNewPricingPlan((current) => {
      const currentSet = Array.isArray(current.moduleSet) ? current.moduleSet : [];
      const nextSet = currentSet.includes(moduleKey)
        ? currentSet.filter((item) => item !== moduleKey)
        : [...currentSet, moduleKey];
      return { ...current, moduleSet: nextSet };
    });
  };

  const handleCreatePricingPlan = async (e) => {
    e.preventDefault();
    try {
      setPricingCreateLoading(true);
      setPricingError(null);
      setPricingSuccess(null);

      if (!String(newPricingPlan.title || '').trim()) {
        setPricingError('Plan basligi zorunludur.');
        return;
      }
      if (!Array.isArray(newPricingPlan.moduleSet) || !newPricingPlan.moduleSet.length) {
        setPricingError('En az bir modul secmelisiniz.');
        return;
      }

      const res = await fetch(`${API_BASE_URL}/api/admin/pricing-plans`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAdminHeaders() },
        body: JSON.stringify({
          planKey: normalizePlanKeyText(newPricingPlan.planKey || '') || null,
          title: String(newPricingPlan.title || '').trim(),
          description: String(newPricingPlan.description || '').trim() || null,
          moduleSet: newPricingPlan.moduleSet,
          durationMonths: Number(newPricingPlan.durationMonths),
          amountTRY: Number(newPricingPlan.amountTRY),
          currency: 'TRY',
          isActive: !!newPricingPlan.isActive,
          sortOrder: Number(newPricingPlan.sortOrder || 0)
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || data.message || 'Fiyat plani olusturulamadi');

      setPricingSuccess(`"${data?.plan?.title || newPricingPlan.title}" plani eklendi.`);
      setNewPricingPlan(INITIAL_NEW_PRICING_PLAN);
      setPricingCreateOpen(false);
      await loadPricingPlans();
    } catch (err) {
      console.error('admin pricing create error', err);
      setPricingError(err.message || 'Fiyat plani olusturulamadi');
    } finally {
      setPricingCreateLoading(false);
    }
  };

  const handleSavePlan = async (plan) => {
    try {
      setPricingSaveId(plan.id);
      setPricingError(null);
      setPricingSuccess(null);
      const res = await fetch(`${API_BASE_URL}/api/admin/pricing-plans/${plan.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAdminHeaders() },
        body: JSON.stringify({
          title: plan.title,
          description: plan.description || null,
          amountTRY: Number(plan.amountTRY),
          isActive: !!plan.isActive,
          sortOrder: Number(plan.sortOrder || 0)
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || data.message || 'Fiyat plani kaydedilemedi');
      setPricingPlans((current) => current.map((item) => (item.id === plan.id ? { ...item, ...data.plan } : item)));
      setPricingSuccess(`"${plan.title}" planı güncellendi.`);
    } catch (err) {
      console.error('admin pricing save error', err);
      setPricingError(err.message || 'Fiyat plani kaydedilemedi');
    } finally {
      setPricingSaveId(null);
    }
  };

  const handleDeletePricingPlan = async (plan) => {
    const sure = window.confirm(`"${plan.title || plan.planKey}" planini silmek istiyor musunuz?`);
    if (!sure) return;

    try {
      setPricingDeleteId(plan.id);
      setPricingError(null);
      setPricingSuccess(null);
      const res = await fetch(`${API_BASE_URL}/api/admin/pricing-plans/${plan.id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { ...getAdminHeaders() }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || data.error || 'Fiyat plani silinemedi');
      setPricingPlans((current) => current.filter((item) => item.id !== plan.id));
      setPricingSuccess(`"${plan.title || plan.planKey}" plani silindi.`);
    } catch (err) {
      console.error('admin pricing delete error', err);
      setPricingError(err.message || 'Fiyat plani silinemedi');
    } finally {
      setPricingDeleteId(null);
    }
  };

  const handleProPlanFieldChange = (planId, field, value) => {
    setProPlanCatalog((current) =>
      current.map((plan) => (plan.id === planId ? { ...plan, [field]: value } : plan))
    );
  };

  const handleSaveProPlan = async (plan) => {
    try {
      setProPlanSaveId(plan.id);
      setProPlanError(null);
      setProPlanSuccess(null);
      const res = await fetch(`${API_BASE_URL}/api/admin/pro-plan-catalog/${plan.id}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAdminHeaders() },
        body: JSON.stringify({
          displayName: plan.displayName,
          maxActiveClientCount: Number(plan.maxActiveClientCount),
          price: Number(plan.price),
          currency: String(plan.currency || 'TRY'),
          billingCycleDays: Number(plan.billingCycleDays || 30),
          isActive: !!plan.isActive,
          sortOrder: Number(plan.sortOrder || 0)
        })
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Profesyonel plan kaydedilemedi');
      setProPlanCatalog((current) =>
        current.map((item) => (item.id === plan.id ? { ...item, ...data.plan } : item))
      );
      setProPlanSuccess(`"${plan.displayName}" plani guncellendi.`);
    } catch (err) {
      console.error('admin pro plan save error', err);
      setProPlanError(err.message || 'Profesyonel plan kaydedilemedi');
    } finally {
      setProPlanSaveId(null);
    }
  };

  const handleDeleteProPlan = async (plan) => {
    const sure = window.confirm(`"${plan.displayName || plan.planCode}" planini silmek istiyor musunuz?`);
    if (!sure) return;

    try {
      setProPlanSaveId(plan.id);
      setProPlanError(null);
      setProPlanSuccess(null);
      const res = await fetch(`${API_BASE_URL}/api/admin/pro-plan-catalog/${plan.id}`, {
        method: 'DELETE',
        credentials: 'include',
        headers: { ...getAdminHeaders() }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.message || data.error || 'Profesyonel plan silinemedi');

      setProPlanCatalog((current) => current.filter((item) => item.id !== plan.id));
      setProPlanSuccess(`"${plan.displayName || plan.planCode}" plani silindi.`);
    } catch (err) {
      console.error('admin pro plan delete error', err);
      setProPlanError(err.message || 'Profesyonel plan silinemedi');
    } finally {
      setProPlanSaveId(null);
    }
  };

  const handleProSubscriptionAction = async (subscriptionId, action) => {
    try {
      setProOpsError(null);
      setActionLoadingId(`pro-sub:${subscriptionId}:${action}`);
      const res = await fetch(`${API_BASE_URL}/api/admin/pro-subscriptions/${subscriptionId}/${action}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAdminHeaders() }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Profesyonel abonelik güncellenemedi');
      await Promise.all([loadProfessionalOps(), loadProKpiOverview()]);
    } catch (err) {
      console.error('admin pro subscription action error', err);
      setProOpsError(err.message || 'Profesyonel abonelik güncellenemedi');
    } finally {
      setActionLoadingId(null);
    }
  };

  const handlePaymentAction = async (intentId, action, body = null) => {
    try {
      setPaymentActionLoadingId(`${intentId}:${action}`);
      setPaymentError(null);
      const res = await fetch(`${API_BASE_URL}/api/admin/payment-intents/${intentId}/${action}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAdminHeaders() },
        body: body ? JSON.stringify(body) : JSON.stringify({})
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || data.message || 'Ödeme aksiyonu uygulanamadı');
      await loadPaymentIntents();
      await loadPaymentNotificationCount();
      window.dispatchEvent(new CustomEvent('refreshNotifications'));
    } catch (err) {
      console.error('admin payment action error', err);
      setPaymentError(err.message || 'Ödeme aksiyonu uygulanamadı');
    } finally {
      setPaymentActionLoadingId(null);
    }
  };

  const getSpecialAccessStatusTone = (status) => {
    if (status === 'available') return 'bg-sky-100 text-sky-700 dark:bg-sky-900/20 dark:text-sky-300';
    if (status === 'active') return 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300';
    if (status === 'consumed') return 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-300';
    return 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-300';
  };

  const handleSpecialAccessAction = async (accessId, action) => {
    try {
      setSpecialAccessActionLoadingId(`${accessId}:${action}`);
      const res = await fetch(`${API_BASE_URL}/api/admin/special-kitchen-accesses/${accessId}/${action}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...getAdminHeaders() }
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.error || 'Özel Günler hakkı güncellenemedi');
      await loadSpecialAccesses();
    } catch (err) {
      console.error('admin special access action error', err);
      setSpecialAccessError(err.message || 'Özel Günler hakkı güncellenemedi');
    } finally {
      setSpecialAccessActionLoadingId(null);
    }
  };

  return (
    <div className="flex h-screen w-full">
      <AdminSidebar mobileOpen={mobileSidebarOpen} onCloseMobile={() => setMobileSidebarOpen(false)} />
      <main className="relative flex h-full flex-1 flex-col overflow-hidden bg-slate-50 transition-[margin] duration-300 dark:bg-slate-900 md:ml-[var(--admin-sidebar-width,18rem)]">
        <AdminTopBar title="Abonelik Yönetimi" onToggleMobile={() => setMobileSidebarOpen((open) => !open)} />

        <div className="flex-1 overflow-y-auto p-8">
          <div className="mx-auto max-w-[1380px] space-y-8 pb-10">
            <div className="flex flex-col gap-3">
              <div>
                <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-white">Abonelik ve Ödeme Operasyonları</h1>
                <p className="mt-2 max-w-3xl text-sm text-slate-500 dark:text-slate-400">
                  Aynı ekrandan premium grant ver, EFT ödeme akışlarını izle ve fiyat planlarını tek kaynaktan güncelle.
                  Buradaki fiyat listesi ileride tüm ödeme kanallarının okuyacağı ana truth source olacak.
                </p>
              </div>

              <div className="flex flex-wrap gap-2 rounded-2xl border border-slate-200 bg-slate-100 p-2 dark:border-slate-800 dark:bg-slate-950/60">
                {TAB_ITEMS.map((tab) => (
                  <TabButton key={tab.key} active={activeTab === tab.key} onClick={() => setActiveTab(tab.key)}>
                    <span className="inline-flex items-center gap-2">
                      <span>{tab.label}</span>
                      {tab.key === 'payments' && paymentUnreadCount > 0 ? (
                        <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-amber-500 px-1 text-[10px] font-black text-slate-950 shadow-sm">
                          {paymentUnreadCount > 9 ? '9+' : paymentUnreadCount}
                        </span>
                      ) : null}
                      {tab.key === 'proKpi' && professionalUnreadCount > 0 ? (
                        <span className="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-cyan-500 px-1 text-[10px] font-black text-slate-950 shadow-sm">
                          {professionalUnreadCount > 9 ? '9+' : professionalUnreadCount}
                        </span>
                      ) : null}
                    </span>
                  </TabButton>
                ))}
              </div>
            </div>

            {activeTab === 'subscriptions' && (
              <>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                  <StatCard title="Toplam Kayıt" value={summary?.total ?? '...'} />
                  <StatCard title="Aktif Abonelik" value={summary?.active ?? '...'} tone="green" />
                  <StatCard title="7 Gün İçinde Bitecek" value={summary?.expiringSoon ?? '...'} tone="amber" />
                  <StatCard title="Bitmiş / İptal" value={`${summary?.expired ?? '...'} / ${summary?.cancelled ?? '...'}`} tone="red" />
                </div>

                <SectionCard title="Yeni Abonelik Tanımla" description="Tek kullanıcı seçip EFT referansıyla premium mutfak aç. Bu alan hızlı operasyon için tek kullanıcılı grant aracıdır.">
                  <form onSubmit={handleCreate} className="grid grid-cols-1 gap-4 xl:grid-cols-6">
                    <div className="xl:col-span-2">
                      <label className="mb-1.5 block text-sm font-medium text-slate-600 dark:text-slate-300">Kullanıcı</label>
                      <input className="mb-2 h-[42px] w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white" placeholder="Kullanıcı ara: ad, e-posta, id" value={userSearch} onChange={(e) => setUserSearch(e.target.value)} />
                      <select className="h-[42px] w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white" value={createForm.userId} onChange={(e) => setCreateForm((prev) => ({ ...prev, userId: e.target.value }))}>
                        <option value="">Kullanıcı seç</option>
                        {filteredUsers.map((user) => <option key={user.id} value={user.id}>{user.name || 'İsimsiz'} - {user.email || `#${user.id}`}</option>)}
                      </select>
                      <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">{filteredUsers.length} kullanıcı gösteriliyor</p>
                    </div>

                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-600 dark:text-slate-300">Modül</label>
                      <select className="h-[42px] w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white" value={createForm.moduleKey} onChange={(e) => setCreateForm((prev) => ({ ...prev, moduleKey: e.target.value }))}>
                        {Object.entries(GRANTABLE_MODULE_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                      </select>
                    </div>

                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-600 dark:text-slate-300">Hedef Üye</label>
                      <input
                        className="h-[42px] w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                        placeholder="household | me | partner | child_0"
                        value={createForm.targetMemberKey}
                        onChange={(e) => {
                          const value = e.target.value || 'household';
                          setCreateForm((prev) => ({
                            ...prev,
                            targetMemberKey: value,
                            scopeType: value === 'household' ? 'household' : 'member'
                          }));
                        }}
                      />
                    </div>

                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-600 dark:text-slate-300">Kapsam</label>
                      <select
                        className="h-[42px] w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white"
                        value={createForm.scopeType}
                        onChange={(e) => setCreateForm((prev) => ({ ...prev, scopeType: e.target.value }))}
                      >
                        <option value="household">Hane Geneli</option>
                        <option value="member">Üye Bazlı</option>
                      </select>
                    </div>

                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-600 dark:text-slate-300">Süre</label>
                      <select className="h-[42px] w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white" value={createForm.durationPreset} onChange={(e) => setCreateForm((prev) => ({ ...prev, durationPreset: e.target.value }))}>
                        <option value="1">1 ay</option>
                        <option value="3">3 ay</option>
                        <option value="6">6 ay</option>
                        <option value="12">12 ay</option>
                        <option value="custom">Özel tarih</option>
                      </select>
                    </div>

                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-600 dark:text-slate-300">Ödeme Tipi</label>
                      <select className="h-[42px] w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white" value={createForm.billingType} onChange={(e) => setCreateForm((prev) => ({ ...prev, billingType: e.target.value }))}>
                        {Object.entries(BILLING_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                      </select>
                    </div>

                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-600 dark:text-slate-300">Başlangıç</label>
                      <input type="datetime-local" className="h-[42px] w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white" value={createForm.startedAt} onChange={(e) => setCreateForm((prev) => ({ ...prev, startedAt: e.target.value }))} />
                    </div>

                    {createForm.durationPreset === 'custom' && (
                      <div>
                        <label className="mb-1.5 block text-sm font-medium text-slate-600 dark:text-slate-300">Bitiş</label>
                        <input type="datetime-local" className="h-[42px] w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white" value={createForm.expiresAt} onChange={(e) => setCreateForm((prev) => ({ ...prev, expiresAt: e.target.value }))} />
                      </div>
                    )}

                    <div className="xl:col-span-2">
                      <label className="mb-1.5 block text-sm font-medium text-slate-600 dark:text-slate-300">EFT Referansı</label>
                      <input className="h-[42px] w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white" placeholder="Dekont no / açıklama" value={createForm.paymentReference} onChange={(e) => setCreateForm((prev) => ({ ...prev, paymentReference: e.target.value }))} />
                    </div>

                    <div className="xl:col-span-3">
                      <label className="mb-1.5 block text-sm font-medium text-slate-600 dark:text-slate-300">Not</label>
                      <textarea rows={2} className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white" placeholder="Örn: EFT dekontu WhatsApp ile doğrulandı" value={createForm.note} onChange={(e) => setCreateForm((prev) => ({ ...prev, note: e.target.value }))} />
                    </div>

                    <div className="xl:col-span-3 flex items-end justify-end gap-3">
                      {createSuccess ? <div className="text-sm font-medium text-emerald-600 dark:text-emerald-400">{createSuccess}</div> : null}
                      <button type="submit" disabled={createLoading} className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-4 py-3 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-200">
                        {createLoading ? 'Tanımlanıyor...' : 'Aboneliği Tanımla'}
                      </button>
                    </div>
                  </form>
                </SectionCard>

                <div className="flex flex-wrap gap-2">
                  <button onClick={() => setQuickFilter('all')} className={`rounded-full px-4 py-2 text-sm font-semibold ${quickFilter === 'all' ? 'bg-slate-900 text-white dark:bg-white dark:text-slate-900' : 'bg-white text-slate-600 dark:bg-slate-900 dark:text-slate-300'}`}>Tüm Kayıtlar</button>
                  <button onClick={() => { setQuickFilter('all'); setFilters((prev) => ({ ...prev, status: 'active' })); }} className="rounded-full bg-emerald-50 px-4 py-2 text-sm font-semibold text-emerald-700 dark:bg-emerald-900/15 dark:text-emerald-300">Sadece Aktifler</button>
                  <button onClick={() => { setQuickFilter('all'); setFilters((prev) => ({ ...prev, status: 'active', billingType: 'eft' })); }} className="rounded-full bg-sky-50 px-4 py-2 text-sm font-semibold text-sky-700 dark:bg-sky-900/15 dark:text-sky-300">Aktif + EFT</button>
                  <button onClick={() => { setQuickFilter('expiringSoon'); setFilters((prev) => ({ ...prev, status: 'active' })); }} className="rounded-full bg-amber-50 px-4 py-2 text-sm font-semibold text-amber-700 dark:bg-amber-900/15 dark:text-amber-300">7 Gün İçinde Bitecekler</button>
                  <button onClick={() => { setQuickFilter('all'); setFilters((prev) => ({ ...prev, status: 'expired' })); }} className="rounded-full bg-red-50 px-4 py-2 text-sm font-semibold text-red-700 dark:bg-red-900/15 dark:text-red-300">Süresi Dolmuşlar</button>
                  <button onClick={() => { setQuickFilter('all'); setFilters({ status: 'all', moduleKey: 'all', billingType: 'all', search: '' }); }} className="rounded-full bg-white px-4 py-2 text-sm font-semibold text-slate-600 dark:bg-slate-900 dark:text-slate-300">Filtreleri Sıfırla</button>
                </div>

                <SectionCard title="Abonelik Operasyon Listesi" description="Aktif, bitecek ve kapanan grant kayitlarini tek tabloda yonet.">
                  <div className="grid grid-cols-1 gap-4 pb-5 lg:grid-cols-4">
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-600 dark:text-slate-300">Durum</label>
                      <select className="h-[42px] w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white" value={filters.status} onChange={(e) => setFilters((prev) => ({ ...prev, status: e.target.value }))}>
                        <option value="all">Tüm durumlar</option>
                        <option value="active">active</option>
                        <option value="expired">expired</option>
                        <option value="cancelled">cancelled</option>
                        <option value="pending">pending</option>
                      </select>
                    </div>
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-600 dark:text-slate-300">Modül</label>
                      <select className="h-[42px] w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white" value={filters.moduleKey} onChange={(e) => setFilters((prev) => ({ ...prev, moduleKey: e.target.value }))}>
                        <option value="all">Tüm modüller</option>
                        {Object.entries(MODULE_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-600 dark:text-slate-300">Ödeme Tipi</label>
                      <select className="h-[42px] w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white" value={filters.billingType} onChange={(e) => setFilters((prev) => ({ ...prev, billingType: e.target.value }))}>
                        <option value="all">Hepsi</option>
                        {Object.entries(BILLING_LABELS).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-600 dark:text-slate-300">Ara</label>
                      <input className="h-[42px] w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white" placeholder="Ad, e-posta, EFT referans..." value={filters.search} onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))} />
                    </div>
                  </div>

                  {error ? <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/30 dark:bg-red-900/10 dark:text-red-300">{error}</div> : null}
                  {loading ? (
                    <div className="py-10 text-sm text-slate-500 dark:text-slate-400">Yükleniyor...</div>
                  ) : visibleGrants.length === 0 ? (
                    <div className="py-10 text-sm text-slate-500 dark:text-slate-400">Filtrelere uygun abonelik bulunamadi.</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800">
                        <thead className="bg-slate-50 dark:bg-slate-900/50">
                          <tr>
                            <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Kullanici</th>
                            <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Modul</th>
                            <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Hedef</th>
                            <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Durum</th>
                            <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Baslangic</th>
                            <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Bitis</th>
                            <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Kalan Gun</th>
                            <th className="px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Odeme</th>
                            <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Islem</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                          {visibleGrants.map((grant) => {
                            const remaining = getDaysRemaining(grant.expiresAt);
                            const isExpiringSoon = grant.status === 'active' && remaining !== null && remaining <= 7;
                            return (
                              <tr key={grant.id} className="hover:bg-slate-50 dark:hover:bg-slate-900/40">
                                <td className="px-5 py-4"><div className="text-sm font-semibold text-slate-900 dark:text-white">{grant.user?.name || '-'}</div><div className="text-xs text-slate-500 dark:text-slate-400">{grant.user?.email || '-'}</div></td>
                                <td className="px-5 py-4 text-sm text-slate-700 dark:text-slate-300">{MODULE_LABELS[grant.moduleKey] || grant.moduleKey}</td>
                                <td className="px-5 py-4">
                                  <div className="text-sm text-slate-700 dark:text-slate-300">{grant.targetMemberKey || 'household'}</div>
                                  <div className="text-xs text-slate-500 dark:text-slate-400">{SCOPE_LABELS[grant.scopeType] || grant.scopeType || 'Hane Geneli'}</div>
                                </td>
                                <td className="px-5 py-4"><span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ${grant.status === 'active' ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-300' : grant.status === 'cancelled' ? 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-300' : 'bg-red-100 text-red-700 dark:bg-red-900/20 dark:text-red-300'}`}>{grant.status}</span></td>
                                <td className="px-5 py-4 text-sm text-slate-700 dark:text-slate-300">{formatDateTime(grant.startedAt)}</td>
                                <td className="px-5 py-4 text-sm text-slate-700 dark:text-slate-300">{formatDateTime(grant.expiresAt)}</td>
                                <td className="px-5 py-4"><span className={`text-sm font-semibold ${isExpiringSoon ? 'text-red-600 dark:text-red-400' : 'text-slate-700 dark:text-slate-300'}`}>{remaining ?? '-'}</span></td>
                                <td className="px-5 py-4"><div className="text-sm text-slate-700 dark:text-slate-300">{BILLING_LABELS[grant.billingType] || grant.billingType}</div><div className="text-xs text-slate-500 dark:text-slate-400">{grant.paymentReference || '-'}</div></td>
                                <td className="px-5 py-4 text-right">
                                  <div className="flex justify-end gap-2">
                                    <button onClick={() => handleOpenDetail(grant.id)} className="inline-flex items-center gap-1 rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800">Detay</button>
                                    <button onClick={() => handleExtend(grant.id, 1)} disabled={actionLoadingId === `${grant.id}:1`} className="inline-flex items-center gap-1 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 dark:border-emerald-900/30 dark:bg-emerald-900/10 dark:text-emerald-300 disabled:opacity-60">{actionLoadingId === `${grant.id}:1` ? 'Uzatiliyor...' : grant.status === 'active' ? '+1 Ay' : '1 Ay Yenile'}</button>
                                    {grant.status === 'active' ? <button onClick={() => handleExtend(grant.id, 3)} disabled={actionLoadingId === `${grant.id}:3`} className="inline-flex items-center gap-1 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs font-semibold text-sky-700 hover:bg-sky-100 dark:border-sky-900/30 dark:bg-sky-900/10 dark:text-sky-300 disabled:opacity-60">{actionLoadingId === `${grant.id}:3` ? 'Uzatiliyor...' : '+3 Ay'}</button> : null}
                                    {grant.status === 'active' ? <button onClick={() => handleCancel(grant.id)} disabled={actionLoadingId === grant.id} className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700 hover:bg-red-100 dark:border-red-900/30 dark:bg-red-900/10 dark:text-red-300 disabled:opacity-60">{actionLoadingId === grant.id ? 'Iptal...' : 'Iptal Et'}</button> : null}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </SectionCard>
              </>
            )}

            {activeTab === 'proKpi' && (
              <>
                <SectionCard
                  title="Profesyonel KPI Özeti"
                  description="Diyetisyen/PT tarafındaki aktivasyon, gelir ve operasyon metrikleri."
                  right={null}
                >
                  <ProfessionalKpiCards
                    metrics={proKpi}
                    loading={proKpiLoading}
                    error={proKpiError}
                    onRefresh={loadProKpiOverview}
                    hideHeader={true}
                  />
                </SectionCard>

                <SectionCard
                  title="Profesyonel Operasyon Özeti"
                  description="Toplam diyetisyen/PT sayıları, doğrulama durumu ve aktif danışan ilişkileri."
                  right={<button onClick={loadProfessionalOps} disabled={proOpsLoading} className="inline-flex items-center rounded-xl border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800">{proOpsLoading ? 'Yükleniyor...' : 'Yenile'}</button>}
                >
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-3 xl:grid-cols-7">
                    <StatCard title="Toplam Uzman" value={proOpsSummary.total} />
                    <StatCard title="Diyetisyen" value={proOpsSummary.dietitianCount} tone="blue" />
                    <StatCard title="PT" value={proOpsSummary.ptCount} tone="blue" />
                    <StatCard title="Doğrulama Bekleyen" value={proOpsSummary.pendingCount} tone="amber" />
                    <StatCard title="Doğrulanmış" value={proOpsSummary.verifiedCount} tone="green" />
                    <StatCard title="Aktif Pro Abonelik" value={proOpsSummary.activeSubscriptionCount} tone="green" />
                    <StatCard title="Aktif Danışan İlişkisi" value={proOpsSummary.activeRelationshipCount} tone="slate" />
                  </div>

                  {proOpsError ? (
                    <div className="mt-4 rounded-lg border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:border-rose-900/30 dark:bg-rose-900/10 dark:text-rose-300">
                      {proOpsError}
                    </div>
                  ) : null}
                </SectionCard>

                <SectionCard
                  title="Profesyonel Abonelik Onayları"
                  description="Profesyonel taraftaki ödeme/abonelik durumunu bu ekrandan onayla veya askıya al."
                >
                  <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800">
                      <thead className="bg-slate-50 dark:bg-slate-900/50">
                        <tr>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Uzman</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Plan</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Durum</th>
                          <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Başlangıç</th>
                          <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">İşlem</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                        {proSubscriptions.map((item) => {
                          const isActive = item.status === 'active';
                          const action = isActive ? 'suspend' : 'resume';
                          const actionLabel = isActive ? 'Askıya Al' : 'Onayla / Aktifleştir';
                          const loadingKey = `pro-sub:${item.id}:${action}`;
                          return (
                            <tr key={item.id} className="hover:bg-slate-50 dark:hover:bg-slate-900/40">
                              <td className="px-4 py-3">
                                <div className="text-sm font-semibold text-slate-900 dark:text-white">{item?.professional?.fullName || '-'}</div>
                                <div className="text-xs text-slate-500 dark:text-slate-400">{item?.professional?.user?.email || '-'}</div>
                              </td>
                              <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-300">{item?.plan?.displayName || item?.planCode || '-'}</td>
                              <td className="px-4 py-3 text-sm text-slate-700 dark:text-slate-300">{item?.status || '-'}</td>
                              <td className="px-4 py-3 text-sm text-slate-500 dark:text-slate-400">{formatDateTime(item?.startsAt)}</td>
                              <td className="px-4 py-3 text-right">
                                <button
                                  onClick={() => handleProSubscriptionAction(item.id, action)}
                                  disabled={actionLoadingId === loadingKey}
                                  className="inline-flex items-center rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                                >
                                  {actionLoadingId === loadingKey ? 'İşleniyor...' : actionLabel}
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                        {proSubscriptions.length === 0 ? (
                          <tr>
                            <td className="px-4 py-4 text-sm text-slate-500 dark:text-slate-400" colSpan={5}>
                              Profesyonel abonelik kaydı bulunamadı.
                            </td>
                          </tr>
                        ) : null}
                      </tbody>
                    </table>
                  </div>
                </SectionCard>
              </>
            )}

            {activeTab === 'payments' && (
              <>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                  <StatCard title="Odeme Bekleniyor" value={paymentSummary?.pending ?? '...'} />
                  <StatCard title="Dekont Yuklendi" value={paymentSummary?.receiptUploaded ?? '...'} tone="blue" />
                  <StatCard title="Manual Review" value={paymentSummary?.manualReview ?? '...'} tone="amber" />
                  <StatCard title="Dogrulandi" value={paymentSummary?.verified ?? '...'} tone="green" />
                </div>

                <SectionCard title="Odeme Talepleri" description="EFT intentleri, bekleyen odemeler ve yakinda dekont akisi burada takip edilecek.">
                  <div className="grid grid-cols-1 gap-4 pb-5 lg:grid-cols-3">
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-600 dark:text-slate-300">Durum</label>
                      <select className="h-[42px] w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white" value={paymentFilters.status} onChange={(e) => setPaymentFilters((prev) => ({ ...prev, status: e.target.value }))}>
                        <option value="all">Tum durumlar</option>
                        <option value="pending_payment">pending_payment</option>
                        <option value="receipt_uploaded">receipt_uploaded</option>
                        <option value="manual_review">manual_review</option>
                        <option value="verified">verified</option>
                        <option value="expired">expired</option>
                        <option value="cancelled">cancelled</option>
                      </select>
                    </div>
                    <div className="lg:col-span-2">
                      <label className="mb-1.5 block text-sm font-medium text-slate-600 dark:text-slate-300">Ara</label>
                      <input className="h-[42px] w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white" placeholder="Kod, plan, ad veya e-posta ara" value={paymentFilters.search} onChange={(e) => setPaymentFilters((prev) => ({ ...prev, search: e.target.value }))} />
                    </div>
                  </div>

                  {paymentError ? <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/30 dark:bg-red-900/10 dark:text-red-300">{paymentError}</div> : null}

                  {paymentLoading ? (
                    <div className="py-10 text-sm text-slate-500 dark:text-slate-400">Odeme talepleri yukleniyor...</div>
                  ) : paymentIntents.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-5 py-10 text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-900/50 dark:text-slate-400">
                      Henuz odeme intenti yok. Premium akista ilk PaymentIntent olusturuldugunda burada listelenecek.
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800">
                        <thead className="bg-slate-50 dark:bg-slate-900/50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Kullanici</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Plan</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Hedef</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Kod</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Tutar</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Durum</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Son Tarih</th>
                            <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Islem</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                          {paymentIntents.map((intent) => (
                            <tr key={intent.id} className="hover:bg-slate-50 dark:hover:bg-slate-900/40">
                              <td className="px-4 py-4">
                                <div className="text-sm font-semibold text-slate-900 dark:text-white">{intent.user?.name || '-'}</div>
                                <div className="text-xs text-slate-500 dark:text-slate-400">{intent.user?.email || '-'}</div>
                              </td>
                              <td className="px-4 py-4">
                                <div className="text-sm font-semibold text-slate-900 dark:text-white">{intent.planTitle || intent.planKey}</div>
                                <div className="text-xs text-slate-500 dark:text-slate-400">{intent.durationMonths} ay</div>
                              </td>
                              <td className="px-4 py-4 text-sm text-slate-700 dark:text-slate-300">{intent.targetMemberKey || 'household'}</td>
                              <td className="px-4 py-4"><span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-200">{intent.intentCode}</span></td>
                              <td className="px-4 py-4 text-sm font-semibold text-slate-900 dark:text-white">{Number(intent.amountTRY || 0).toFixed(2)} TL</td>
                              <td className="px-4 py-4 text-sm text-slate-700 dark:text-slate-300">{intent.status}</td>
                              <td className="px-4 py-4 text-sm text-slate-700 dark:text-slate-300">{formatDateTime(intent.expiresAt)}</td>
                              <td className="px-4 py-4 text-right">
                                <div className="flex justify-end gap-2">
                                  <button
                                    onClick={() => handlePaymentAction(intent.id, 'reprocess')}
                                    disabled={paymentActionLoadingId === `${intent.id}:reprocess`}
                                    className="inline-flex items-center gap-2 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs font-semibold text-sky-700 hover:bg-sky-100 disabled:opacity-60 dark:border-sky-900/30 dark:bg-sky-900/10 dark:text-sky-300"
                                  >
                                    {paymentActionLoadingId === `${intent.id}:reprocess` ? 'Isleniyor...' : 'Tekrar Islet'}
                                  </button>
                                  {intent.status !== 'verified' ? (
                                    <button
                                      onClick={() => handlePaymentAction(intent.id, 'manual-approve')}
                                      disabled={paymentActionLoadingId === `${intent.id}:manual-approve`}
                                      className="inline-flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 disabled:opacity-60 dark:border-emerald-900/30 dark:bg-emerald-900/10 dark:text-emerald-300"
                                    >
                                      {paymentActionLoadingId === `${intent.id}:manual-approve` ? 'Onaylaniyor...' : 'Manuel Onay'}
                                    </button>
                                  ) : null}
                                  {intent.status !== 'failed' && intent.status !== 'verified' ? (
                                    <button
                                      onClick={() => handlePaymentAction(intent.id, 'reject', { reason: 'admin_manual_reject' })}
                                      disabled={paymentActionLoadingId === `${intent.id}:reject`}
                                      className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700 hover:bg-red-100 disabled:opacity-60 dark:border-red-900/30 dark:bg-red-900/10 dark:text-red-300"
                                    >
                                      {paymentActionLoadingId === `${intent.id}:reject` ? 'Reddediliyor...' : 'Reddet'}
                                    </button>
                                  ) : null}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </SectionCard>
              </>
            )}

            {activeTab === 'specialAccess' && (
              <>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                  <StatCard title="Toplam Hak" value={specialAccessSummary?.total ?? '...'} />
                  <StatCard title="Bekleyen Hak" value={specialAccessSummary?.available ?? '...'} tone="blue" />
                  <StatCard title="Aktif Organizasyon" value={specialAccessSummary?.active ?? '...'} tone="green" />
                  <StatCard title="Tüketilen" value={specialAccessSummary?.consumed ?? '...'} tone="amber" />
                </div>

                <SectionCard title="Özel Günler Hakları" description="Tek satın alımla açılan Özel Günler erişim hakları burada izlenir. Hak organizasyon başlayınca aktif olur, etkinlik bittiğinde tüketilir.">
                  <div className="grid grid-cols-1 gap-4 pb-5 lg:grid-cols-3">
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-slate-600 dark:text-slate-300">Durum</label>
                      <select className="h-[42px] w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white" value={specialAccessFilters.status} onChange={(e) => setSpecialAccessFilters((prev) => ({ ...prev, status: e.target.value }))}>
                        <option value="all">Tüm durumlar</option>
                        <option value="available">available</option>
                        <option value="active">active</option>
                        <option value="consumed">consumed</option>
                      </select>
                    </div>
                    <div className="lg:col-span-2">
                      <label className="mb-1.5 block text-sm font-medium text-slate-600 dark:text-slate-300">Ara</label>
                      <input className="h-[42px] w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white" placeholder="Kullanıcı, e-posta, kod veya etkinlik ID ara" value={specialAccessFilters.search} onChange={(e) => setSpecialAccessFilters((prev) => ({ ...prev, search: e.target.value }))} />
                    </div>
                  </div>

                  {specialAccessError ? <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/30 dark:bg-red-900/10 dark:text-red-300">{specialAccessError}</div> : null}

                  {specialAccessLoading ? (
                    <div className="py-10 text-sm text-slate-500 dark:text-slate-400">Özel Günler hakları yükleniyor...</div>
                  ) : specialAccesses.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-5 py-10 text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-900/50 dark:text-slate-400">
                      Henüz Özel Günler hakkı oluşturulmamış. İlk tek kullanımlık ödeme onaylandığında burada görünecek.
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800">
                        <thead className="bg-slate-50 dark:bg-slate-900/50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Kullanıcı</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Durum</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Ödeme</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Etkinlik</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Satın Alım</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Aktifleşme</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Tüketilme</th>
                            <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">İşlem</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                          {specialAccesses.map((access) => (
                            <tr key={access.id} className="hover:bg-slate-50 dark:hover:bg-slate-900/40">
                              <td className="px-4 py-4">
                                <div className="text-sm font-semibold text-slate-900 dark:text-white">{access.user?.name || '-'}</div>
                                <div className="text-xs text-slate-500 dark:text-slate-400">{access.user?.email || '-'}</div>
                              </td>
                              <td className="px-4 py-4">
                                <span className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold ${getSpecialAccessStatusTone(access.status)}`}>{access.status}</span>
                              </td>
                              <td className="px-4 py-4">
                                <div className="text-sm font-semibold text-slate-900 dark:text-white">{access.paymentIntentCode || '-'}</div>
                                <div className="text-xs text-slate-500 dark:text-slate-400">{access.amountTRY != null ? `${Number(access.amountTRY).toFixed(2)} TL` : '-'}</div>
                              </td>
                              <td className="px-4 py-4 text-sm text-slate-700 dark:text-slate-300">{access.eventId || '-'}</td>
                              <td className="px-4 py-4 text-sm text-slate-700 dark:text-slate-300">{formatDateTime(access.createdAt)}</td>
                              <td className="px-4 py-4 text-sm text-slate-700 dark:text-slate-300">{formatDateTime(access.activatedAt)}</td>
                              <td className="px-4 py-4 text-sm text-slate-700 dark:text-slate-300">{formatDateTime(access.consumedAt)}</td>
                              <td className="px-4 py-4 text-right">
                                <div className="flex justify-end gap-2">
                                  {access.status !== 'available' ? (
                                    <button
                                      onClick={() => handleSpecialAccessAction(access.id, 'reset')}
                                      disabled={specialAccessActionLoadingId === `${access.id}:reset`}
                                      className="inline-flex items-center rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs font-semibold text-sky-700 hover:bg-sky-100 disabled:opacity-60 dark:border-sky-900/30 dark:bg-sky-900/10 dark:text-sky-300"
                                    >
                                      {specialAccessActionLoadingId === `${access.id}:reset` ? 'Sıfırlanıyor...' : 'Tekrar Kullanılabilir Yap'}
                                    </button>
                                  ) : null}
                                  {access.status !== 'consumed' ? (
                                    <button
                                      onClick={() => handleSpecialAccessAction(access.id, 'consume')}
                                      disabled={specialAccessActionLoadingId === `${access.id}:consume`}
                                      className="inline-flex items-center rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-700 hover:bg-amber-100 disabled:opacity-60 dark:border-amber-900/30 dark:bg-amber-900/10 dark:text-amber-300"
                                    >
                                      {specialAccessActionLoadingId === `${access.id}:consume` ? 'İşleniyor...' : 'Tüketildi İşaretle'}
                                    </button>
                                  ) : null}
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </SectionCard>
              </>
            )}

            {activeTab === 'pricing' && (
              <>
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                  <StatCard title="Toplam Plan" value={pricingSummary.total} />
                  <StatCard title="Aktif Plan" value={pricingSummary.active} tone="green" />
                  <StatCard title="Tekil Paket" value={pricingSummary.singles} tone="blue" />
                  <StatCard title="Bundle Plan" value={pricingSummary.bundles} tone="amber" />
                </div>

                <SectionCard
                  title="Profesyonel Plan Fiyatlari (Diyetisyen/PT)"
                  description="Diyetisyen ve PT paket fiyatlarini buradan yonet. Bu kisim PRO_5 / PRO_10 / PRO_25 gibi planlari gunceller."
                  right={
                    <button
                      onClick={loadProPlanCatalog}
                      disabled={proPlanLoading}
                      className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                    >
                      {proPlanLoading ? 'Yenileniyor...' : 'Yenile'}
                    </button>
                  }
                >
                  {proPlanError ? <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/30 dark:bg-red-900/10 dark:text-red-300">{proPlanError}</div> : null}
                  {proPlanSuccess ? <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 dark:border-emerald-900/30 dark:bg-emerald-900/10 dark:text-emerald-300">{proPlanSuccess}</div> : null}

                  {proPlanLoading ? (
                    <div className="py-8 text-sm text-slate-500 dark:text-slate-400">Profesyonel planlar yukleniyor...</div>
                  ) : proPlanCatalog.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-5 py-8 text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-900/50 dark:text-slate-400">
                      Henuz profesyonel plan yok. Ilk olarak PRO_5, PRO_10, PRO_25 planlarini olustur.
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800">
                        <thead className="bg-slate-50 dark:bg-slate-900/50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Plan Kodu</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Gorunen Ad</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Danisan Kotasi</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Fiyat</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Doviz</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Dongu (gun)</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Aktif</th>
                            <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">İşlem</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                          {proPlanCatalog.map((plan) => (
                            <tr key={plan.id} className="align-top hover:bg-slate-50 dark:hover:bg-slate-900/40">
                              <td className="px-4 py-4 text-sm font-semibold text-slate-900 dark:text-white">{plan.planCode}</td>
                              <td className="px-4 py-4">
                                <input value={plan.displayName || ''} onChange={(e) => handleProPlanFieldChange(plan.id, 'displayName', e.target.value)} className="h-[40px] w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white" />
                              </td>
                              <td className="px-4 py-4">
                                <input type="number" min="1" step="1" value={plan.maxActiveClientCount ?? 1} onChange={(e) => handleProPlanFieldChange(plan.id, 'maxActiveClientCount', e.target.value)} className="h-[40px] w-28 rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white" />
                              </td>
                              <td className="px-4 py-4">
                                <input type="number" min="0" step="0.01" value={plan.price ?? 0} onChange={(e) => handleProPlanFieldChange(plan.id, 'price', e.target.value)} className="h-[40px] w-32 rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white" />
                              </td>
                              <td className="px-4 py-4">
                                <input value={plan.currency || 'TRY'} onChange={(e) => handleProPlanFieldChange(plan.id, 'currency', e.target.value)} className="h-[40px] w-20 rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white" />
                              </td>
                              <td className="px-4 py-4">
                                <input type="number" min="1" step="1" value={plan.billingCycleDays ?? 30} onChange={(e) => handleProPlanFieldChange(plan.id, 'billingCycleDays', e.target.value)} className="h-[40px] w-24 rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white" />
                              </td>
                              <td className="px-4 py-4"><label className="inline-flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300"><input type="checkbox" checked={!!plan.isActive} onChange={(e) => handleProPlanFieldChange(plan.id, 'isActive', e.target.checked)} />Acik</label></td>
                              <td className="px-4 py-4 text-right">
                                <div className="flex justify-end gap-2">
                                  <button onClick={() => handleSaveProPlan(plan)} disabled={proPlanSaveId === plan.id} className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-800 disabled:opacity-60 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-200">{proPlanSaveId === plan.id ? 'Kaydediliyor...' : 'Kaydet'}</button>
                                  <button onClick={() => handleDeleteProPlan(plan)} disabled={proPlanSaveId === plan.id} className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700 hover:bg-red-100 disabled:opacity-60 dark:border-red-900/30 dark:bg-red-900/10 dark:text-red-300">{proPlanSaveId === plan.id ? 'Bekleyin...' : 'Sil'}</button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </SectionCard>

                <SectionCard
                  title="Fiyat Planlari"
                  description="Buradaki rakamlar premium satis akislarinin tek truth source'u olacak. Fiyat bir kez burada degisir, EFT ve ilerideki diger odeme kanallari ayni plani okur."
                  right={
                    <div className="flex flex-wrap items-center gap-2">
                      <button
                        onClick={() => setPricingCreateOpen((prev) => !prev)}
                        className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                      >
                        {pricingCreateOpen ? 'Formu Kapat' : 'Yeni Plan Ekle'}
                      </button>
                      <button
                        onClick={handleBootstrapPricingPlans}
                        disabled={pricingBootstrapLoading}
                        className="inline-flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                      >
                        {pricingBootstrapLoading ? 'Hazirlaniyor...' : 'Varsayilan Planlari Kur'}
                      </button>
                    </div>
                  }
                >
                  {pricingError ? <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/30 dark:bg-red-900/10 dark:text-red-300">{pricingError}</div> : null}
                  {pricingSuccess ? <div className="mb-4 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700 dark:border-emerald-900/30 dark:bg-emerald-900/10 dark:text-emerald-300">{pricingSuccess}</div> : null}
                  {pricingCreateOpen ? (
                    <form onSubmit={handleCreatePricingPlan} className="mb-6 rounded-xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-900/40">
                      <div className="mb-3 text-sm font-semibold text-slate-900 dark:text-white">Manuel Plan Ekle</div>
                      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                          Plan Key (Opsiyonel)
                          <div className="mt-1 flex gap-2">
                            <input value={newPricingPlan.planKey} onChange={(e) => handleNewPricingPlanChange('planKey', e.target.value)} placeholder="Bos birakirsan otomatik uretilir" className="h-[40px] w-full rounded-lg border border-slate-300 bg-white px-3 text-sm font-normal text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white" />
                            <button type="button" onClick={handleGeneratePlanKey} className="shrink-0 rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800">
                              Otomatik Key
                            </button>
                          </div>
                        </label>
                        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                          Baslik
                          <input value={newPricingPlan.title} onChange={(e) => handleNewPricingPlanChange('title', e.target.value)} placeholder="Ornek Plan" className="mt-1 h-[40px] w-full rounded-lg border border-slate-300 bg-white px-3 text-sm font-normal text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white" />
                        </label>
                        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500 md:col-span-2">
                          Aciklama
                          <input value={newPricingPlan.description} onChange={(e) => handleNewPricingPlanChange('description', e.target.value)} placeholder="Opsiyonel aciklama" className="mt-1 h-[40px] w-full rounded-lg border border-slate-300 bg-white px-3 text-sm font-normal text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white" />
                        </label>
                        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                          Sure (Ay)
                          <input type="number" min="0" step="1" value={newPricingPlan.durationMonths} onChange={(e) => handleNewPricingPlanChange('durationMonths', e.target.value)} className="mt-1 h-[40px] w-full rounded-lg border border-slate-300 bg-white px-3 text-sm font-normal text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white" />
                        </label>
                        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                          Fiyat (TRY)
                          <input type="number" min="0" step="0.01" value={newPricingPlan.amountTRY} onChange={(e) => handleNewPricingPlanChange('amountTRY', e.target.value)} className="mt-1 h-[40px] w-full rounded-lg border border-slate-300 bg-white px-3 text-sm font-normal text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white" />
                        </label>
                        <label className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                          Sira
                          <input type="number" min="0" step="1" value={newPricingPlan.sortOrder} onChange={(e) => handleNewPricingPlanChange('sortOrder', e.target.value)} className="mt-1 h-[40px] w-full rounded-lg border border-slate-300 bg-white px-3 text-sm font-normal text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white" />
                        </label>
                        <label className="mt-5 inline-flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300">
                          <input type="checkbox" checked={!!newPricingPlan.isActive} onChange={(e) => handleNewPricingPlanChange('isActive', e.target.checked)} />
                          Acik baslasin
                        </label>
                      </div>

                      <div className="mt-4">
                        <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Moduller</div>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {PRICING_MODULE_OPTIONS.map((moduleKey) => {
                            const active = Array.isArray(newPricingPlan.moduleSet) && newPricingPlan.moduleSet.includes(moduleKey);
                            return (
                              <button
                                key={moduleKey}
                                type="button"
                                onClick={() => handleNewPricingModuleToggle(moduleKey)}
                                className={`rounded-full border px-3 py-1.5 text-xs font-semibold transition ${
                                  active
                                    ? 'border-slate-900 bg-slate-900 text-white dark:border-white dark:bg-white dark:text-slate-900'
                                    : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-300 dark:hover:bg-slate-800'
                                }`}
                              >
                                {MODULE_LABELS[moduleKey] || moduleKey}
                              </button>
                            );
                          })}
                        </div>
                      </div>

                      <div className="mt-4 flex justify-end gap-2">
                        <button type="button" onClick={() => { setPricingCreateOpen(false); setNewPricingPlan(INITIAL_NEW_PRICING_PLAN); }} className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800">Vazgec</button>
                        <button type="submit" disabled={pricingCreateLoading} className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-800 disabled:opacity-60 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-200">{pricingCreateLoading ? 'Ekleniyor...' : 'Plani Ekle'}</button>
                      </div>
                    </form>
                  ) : null}
                  {pricingLoading ? (
                    <div className="py-10 text-sm text-slate-500 dark:text-slate-400">Fiyat planlari yukleniyor...</div>
                  ) : pricingPlans.length === 0 ? (
                    <div className="rounded-xl border border-dashed border-slate-300 bg-slate-50 px-5 py-10 text-sm text-slate-500 dark:border-slate-700 dark:bg-slate-900/50 dark:text-slate-400">Henuz fiyat plani yok. Once "Varsayilan Planlari Kur" diyerek standart premium listesini olustur.</div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-slate-200 dark:divide-slate-800">
                        <thead className="bg-slate-50 dark:bg-slate-900/50">
                          <tr>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Plan</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Moduller</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Sure</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Fiyat (TRY)</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Aktif</th>
                            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">Sira</th>
                            <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-slate-500">Aksiyon</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-200 dark:divide-slate-800">
                          {pricingPlans.map((plan) => (
                            <tr key={plan.id} className="align-top hover:bg-slate-50 dark:hover:bg-slate-900/40">
                              <td className="px-4 py-4">
                                <input value={plan.title || ''} onChange={(e) => handlePlanFieldChange(plan.id, 'title', e.target.value)} className="mb-2 h-[40px] w-full rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white" />
                                <div className="text-xs text-slate-500 dark:text-slate-400">{plan.planKey}</div>
                              </td>
                              <td className="px-4 py-4 text-sm text-slate-700 dark:text-slate-300">
                                <div className="flex flex-wrap gap-2">
                                  {(plan.moduleSet || []).map((item) => <span key={`${plan.id}:${item}`} className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700 dark:bg-slate-800 dark:text-slate-200">{MODULE_LABELS[item] || item}</span>)}
                                </div>
                              </td>
                              <td className="px-4 py-4 text-sm font-semibold text-slate-900 dark:text-white">{plan.durationMonths} ay</td>
                              <td className="px-4 py-4"><input type="number" min="0" step="0.01" value={plan.amountTRY ?? 0} onChange={(e) => handlePlanFieldChange(plan.id, 'amountTRY', e.target.value)} className="h-[40px] w-32 rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white" /></td>
                              <td className="px-4 py-4"><label className="inline-flex items-center gap-2 text-sm font-medium text-slate-700 dark:text-slate-300"><input type="checkbox" checked={!!plan.isActive} onChange={(e) => handlePlanFieldChange(plan.id, 'isActive', e.target.checked)} />Acik</label></td>
                              <td className="px-4 py-4"><input type="number" min="0" step="1" value={plan.sortOrder ?? 0} onChange={(e) => handlePlanFieldChange(plan.id, 'sortOrder', e.target.value)} className="h-[40px] w-24 rounded-lg border border-slate-300 bg-white px-3 text-sm text-slate-900 dark:border-slate-700 dark:bg-slate-900 dark:text-white" /></td>
                              <td className="px-4 py-4 text-right">
                                <div className="flex justify-end gap-2">
                                  <button onClick={() => handleSavePlan(plan)} disabled={pricingSaveId === plan.id || pricingDeleteId === plan.id} className="inline-flex items-center gap-2 rounded-lg bg-slate-900 px-3 py-2 text-xs font-semibold text-white hover:bg-slate-800 disabled:opacity-60 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-200">{pricingSaveId === plan.id ? 'Kaydediliyor...' : 'Kaydet'}</button>
                                  <button onClick={() => handleDeletePricingPlan(plan)} disabled={pricingDeleteId === plan.id || pricingSaveId === plan.id} className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs font-semibold text-red-700 hover:bg-red-100 disabled:opacity-60 dark:border-red-900/30 dark:bg-red-900/10 dark:text-red-300">{pricingDeleteId === plan.id ? 'Siliniyor...' : 'Sil'}</button>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </SectionCard>
              </>
            )}
          </div>
        </div>
      </main>

      {detailOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-end bg-slate-950/40 backdrop-blur-sm">
          <div className="h-full w-full max-w-2xl overflow-y-auto border-l border-slate-200 bg-white shadow-2xl dark:border-slate-800 dark:bg-[#101922]">
            <div className="flex items-center justify-between border-b border-slate-200 px-6 py-4 dark:border-slate-800">
              <div>
                <h2 className="text-lg font-semibold text-slate-900 dark:text-white">Abonelik Detayi</h2>
                <p className="text-sm text-slate-500 dark:text-slate-400">EFT notu, reminder loglari ve modul gecmisi</p>
              </div>
              <button onClick={() => setDetailOpen(false)} className="rounded-lg border border-slate-300 px-3 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800">Kapat</button>
            </div>
            <div className="space-y-6 p-6">
              {detailLoading ? <div className="text-sm text-slate-500 dark:text-slate-400">Detay yukleniyor...</div> : detailError ? <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900/30 dark:bg-red-900/10 dark:text-red-300">{detailError}</div> : detailData?.grant ? <>
                <div className="rounded-xl border border-slate-200 p-5 dark:border-slate-800">
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    <div><div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Kullanici</div><div className="mt-1 text-sm font-semibold text-slate-900 dark:text-white">{detailData.grant.user?.name || '-'}</div><div className="text-sm text-slate-500 dark:text-slate-400">{detailData.grant.user?.email || '-'}</div></div>
                    <div><div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Modul</div><div className="mt-1 text-sm font-semibold text-slate-900 dark:text-white">{MODULE_LABELS[detailData.grant.moduleKey] || detailData.grant.moduleKey}</div><div className="text-sm text-slate-500 dark:text-slate-400">{BILLING_LABELS[detailData.grant.billingType] || detailData.grant.billingType}</div></div>
                    <div><div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Hedef Üye / Kapsam</div><div className="mt-1 text-sm font-semibold text-slate-900 dark:text-white">{detailData.grant.targetMemberKey || 'household'}</div><div className="text-sm text-slate-500 dark:text-slate-400">{SCOPE_LABELS[detailData.grant.scopeType] || detailData.grant.scopeType || 'Hane Geneli'}</div></div>
                    <div><div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Baslangic / Bitis</div><div className="mt-1 text-sm text-slate-900 dark:text-white">{formatDateTime(detailData.grant.startedAt)}</div><div className="text-sm text-slate-500 dark:text-slate-400">{formatDateTime(detailData.grant.expiresAt)}</div></div>
                    <div><div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Kalan Gun</div><div className="mt-1 text-sm font-semibold text-slate-900 dark:text-white">{getDaysRemaining(detailData.grant.expiresAt) ?? '-'}</div></div>
                  </div>
                </div>
                <div className="rounded-xl border border-slate-200 p-5 dark:border-slate-800"><div className="mb-3 text-sm font-semibold text-slate-900 dark:text-white">Odeme Notlari</div><div className="space-y-2 text-sm text-slate-700 dark:text-slate-300"><div><span className="font-semibold">EFT Referansi:</span> {detailData.grant.paymentReference || '-'}</div><div><span className="font-semibold">Not:</span> {detailData.grant.note || '-'}</div></div></div>
                <div className="rounded-xl border border-slate-200 p-5 dark:border-slate-800"><div className="mb-3 text-sm font-semibold text-slate-900 dark:text-white">Hatirlatma Loglari</div>{detailData.reminderLogs?.length ? <div className="space-y-2">{detailData.reminderLogs.map((log) => <div key={log.id} className="rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700 dark:bg-slate-900 dark:text-slate-300"><span className="font-semibold">{log.type}</span> - {formatDateTime(log.sentAt)}</div>)}</div> : <div className="text-sm text-slate-500 dark:text-slate-400">Henuz reminder gonderilmemis.</div>}</div>
                <div className="rounded-xl border border-slate-200 p-5 dark:border-slate-800"><div className="mb-3 text-sm font-semibold text-slate-900 dark:text-white">Ayni Modul Gecmisi</div>{detailData.history?.length ? <div className="space-y-2">{detailData.history.map((item) => <div key={item.id} className="rounded-lg bg-slate-50 px-3 py-3 text-sm dark:bg-slate-900"><div className="flex items-center justify-between gap-3"><span className="font-semibold text-slate-900 dark:text-white">{item.status}</span><span className="text-slate-500 dark:text-slate-400">{formatDateTime(item.expiresAt)}</span></div><div className="mt-1 text-slate-500 dark:text-slate-400">{item.paymentReference || '-'} {item.note ? `- ${item.note}` : ''}</div></div>)}</div> : <div className="text-sm text-slate-500 dark:text-slate-400">Gecmis kayit bulunamadi.</div>}</div>
              </> : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
